import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import {
  Building2,
  ChevronDown,
  ChevronRight,
  LayoutDashboard,
  Users,
  Warehouse,
  Store,
  Tag,
  FolderTree,
  Scale,
  Receipt,
  Package,
  ShoppingCart,
  Truck,
  BarChart3,
  Settings,
  TrendingDown,
  LogOut,
  Menu,
  X,
  FileText,
  FileCheck,
  ClipboardList,
  CreditCard,
  Percent,
  ArrowRightLeft,
  DollarSign,
  ClipboardEdit,
  KeyRound,
  Activity,
  RefreshCw,
  Calculator,
  Server,
  ShieldAlert,
  Database,
  Calendar,
  Grid3X3,
  MapPin,
  Wallet,
  IndianRupee,
  HardDrive,
  Wrench,
  Undo2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { cn } from '@/lib/utils';
import ChangePasswordDialog from '@/components/ChangePasswordDialog';
import { OnlineOrderBell } from '@/components/hq/ecom/OnlineOrderBell';
import BackupAlert from '@/components/hq/BackupAlert';
import { Toaster } from '@/components/ui/sonner';

const menuItems = [
  {
    title: 'Dashboard',
    icon: LayoutDashboard,
    path: '/hq'
  },
  {
    title: 'Monitoring',
    icon: Activity,
    children: [
      { title: 'Live Sales', path: '/hq/monitoring/live-sales', icon: Activity },
      { title: 'Store Sync Status', path: '/hq/monitoring/sync-status', icon: Server },
      { title: 'Store Health Check', path: '/hq/monitoring/health-check', icon: ShieldAlert }
    ]
  },
  {
    title: 'Organization',
    icon: Building2,
    children: [
      { title: 'Business Settings', path: '/hq/settings/business', icon: Settings },
      { title: 'Warehouses', path: '/hq/warehouses', icon: Warehouse },
      { title: 'Stores', path: '/hq/stores', icon: Store },
      { title: 'Users', path: '/hq/users', icon: Users }
    ]
  },
  {
    title: 'Masters',
    icon: FolderTree,
    children: [
      { title: 'Brands', path: '/hq/brands', icon: Tag },
      { title: 'Categories', path: '/hq/categories', icon: FolderTree },
      { title: 'Units (UOM)', path: '/hq/uom', icon: Scale },
      { title: 'Tax Groups', path: '/hq/tax', icon: Receipt }
    ]
  },
  {
    title: 'Vendors',
    icon: Truck,
    children: [
      { title: 'Suppliers', path: '/hq/vendors', icon: Truck },
      { title: 'Manufacturers', path: '/hq/manufacturers', icon: Building2 },
      { title: 'Local Vendor Monitor', path: '/hq/franchise/local-vendors', icon: Truck }
    ]
  },
  {
    title: 'Revenue',
    icon: DollarSign,
    children: [
      { title: 'Revenue Settings', path: '/hq/revenue/settings', icon: Settings },
      { title: 'Receivable Invoices', path: '/hq/revenue/invoices', icon: FileText },
      { title: 'Receivable Payments', path: '/hq/revenue/payments', icon: CreditCard }
    ]
  },
  {
    title: 'Bookings',
    icon: Calendar,
    children: [
      { title: 'Shelf & Display Booking', path: '/hq/bookings/shelf', icon: Grid3X3 },
      { title: 'Brand Staff Contracts', path: '/hq/bookings/staff', icon: Users },
      { title: 'Booking Settings', path: '/hq/bookings/settings', icon: Settings }
    ]
  },
  {
    title: 'E-Commerce',
    icon: ShoppingCart,
    children: [
      { title: 'Orders', path: '/hq/ecommerce/orders', icon: Package },
      { title: 'Home Page CMS', path: '/hq/ecommerce/cms-home', icon: FileText },
      { title: 'Legal Pages', path: '/hq/ecommerce/legal-pages', icon: FileText },
      { title: 'Store Settings', path: '/hq/ecommerce/store-settings', icon: Settings },
      { title: 'Delivery Zones', path: '/hq/ecommerce/delivery-zones', icon: Truck },
      { title: 'Logistics Partners', path: '/hq/ecommerce/logistics', icon: Package },
      { title: 'Location Database', path: '/hq/ecommerce/locations', icon: MapPin }
    ]
  },
  {
    title: 'Expenses',
    icon: Wallet,
    children: [
      { title: 'Manage Expenses', path: '/hq/expenses', icon: IndianRupee },
      { title: 'Expense Categories', path: '/hq/expenses/categories', icon: FolderTree }
    ]
  },
  {
    title: 'Offers',
    icon: Percent,
    path: '/hq/offers'
  },
  {
    title: 'Purchase',
    icon: ShoppingCart,
    children: [
      { title: 'Purchase Orders', path: '/hq/purchase/orders', icon: FileText },
      { title: 'Goods Receipt (GRN)', path: '/hq/purchase/grn', icon: ClipboardList },
      { title: 'Purchase Returns', path: '/hq/purchase/returns', icon: Undo2 },
      { title: 'Franchise Direct PO', path: '/hq/franchise/direct-po', icon: ShoppingCart },
      { title: 'PO Approvals', path: '/hq/franchise/po-approvals', icon: FileCheck }
    ]
  },
  {
    title: 'Inventory',
    icon: Package,
    children: [
      { title: 'Products', path: '/hq/products', icon: Package },
      { title: 'Warehouse Stock', path: '/hq/inventory/stock', icon: Warehouse },
      { title: 'Store Stocks', path: '/hq/inventory/store-stocks', icon: Store },
      { title: 'Pricing Issues', path: '/hq/inventory/price-settings', icon: DollarSign },
      { title: 'Products Weights', path: '/hq/inventory/product-weights', icon: Scale },
      { title: 'Direct Stock to Store', path: '/hq/inventory/direct-stock', icon: Package },
      { title: 'Sales Stock Repair', path: '/hq/inventory/sales-stock-repair', icon: Wrench },
      { title: 'Stock Requests', path: '/hq/inventory/stock-requests', icon: ClipboardList },
      { title: 'Transfer Orders', path: '/hq/inventory/transfer-orders', icon: Truck },
      { title: 'Stock Transfers', path: '/hq/inventory/transfers', icon: ArrowRightLeft },
      { title: 'Stock Adjustments', path: '/hq/inventory/adjustments', icon: ClipboardEdit }
    ]
  },
  {
    title: 'Ledgers & Payments',
    icon: Wallet,
    children: [
      { title: 'Franchise Ledger', path: '/hq/ledgers/franchise', icon: Store },
      { title: 'Franchise Payments', path: '/hq/payments/franchise', icon: CreditCard },
      { title: 'Franchise Pricing', path: '/hq/franchise/pricing-levels', icon: Percent },
      { title: 'Vendor Ledger', path: '/hq/ledgers/vendor', icon: Truck },
      { title: 'Vendor Payments', path: '/hq/vendor-payments', icon: CreditCard },
      { title: 'Debit / Credit Notes', path: '/hq/ledgers/notes', icon: FileText }
    ]
  },
  {
    title: 'Sync Dashboard',
    icon: RefreshCw,
    path: '/hq/sync-dashboard'
  },
  {
    title: 'Sync Status',
    icon: RefreshCw,
    path: '/hq/sync-status'
  },
  {
    title: 'Customers',
    icon: Users,
    path: '/hq/customers'
  },
  {
    title: 'Reports',
    icon: BarChart3,
    children: [
      { title: 'All Sales', path: '/hq/reports/sales/all' },
      { title: 'Payment Mode wise', path: '/hq/reports/sales/payment-mode' },
      { title: 'Store/Franchise wise', path: '/hq/reports/sales/store-wise' },
      { title: 'Category wise', path: '/hq/reports/sales/category-wise' },
      { title: 'Brand wise', path: '/hq/reports/sales/brand-wise' },
      { title: 'HSN wise', path: '/hq/reports/sales/hsn-wise' },
      { title: 'GST Tax Report', path: '/hq/reports/sales/gst-tax' },
      { title: 'Fast Moving', path: '/hq/reports/sales/fast-moving' },
      { title: 'Slow Moving', path: '/hq/reports/sales/slow-moving' },
      { title: 'Dead Stock', path: '/hq/reports/sales/dead-stock' },
      { title: 'Product wise Sales', path: '/hq/reports/sales/product-wise' },
      { title: 'Profit Analysis', path: '/hq/reports/profit' },
      { title: 'Stock Expiry', path: '/hq/reports/expiry' },
      { title: 'Stock Reports', path: '/hq/reports/stock' },
      { title: 'Audit Logs', path: '/hq/reports/audit' }
    ]
  },
  {
    title: 'Backup',
    icon: Database,
    path: '/hq/backup'
  },
  {
    title: 'Settings',
    icon: Settings,
    children: [
      { title: 'Asset Storage', path: '/hq/settings/asset-storage', icon: Database },
      { title: 'Security Code', path: '/hq/settings/security-code', icon: KeyRound },
      { title: 'Margin Change Log', path: '/hq/margin-log', icon: TrendingDown },
      { title: 'Fix Issues', path: '/hq/settings/fix-issues', icon: Wrench },
      { title: 'Entity Reset', path: '/hq/settings/entity-reset', icon: ShieldAlert },
      { title: 'Stock Audit', path: '/hq/stock-audit', icon: ShieldAlert },
      { title: 'System Reset & Backup', path: '/hq/settings/system', icon: HardDrive }
    ]
  }
];

const HQLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState(['Organization', 'Masters']);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const toggleMenu = (title) => {
    setExpandedMenus(prev =>
      prev.includes(title)
        ? prev.filter(t => t !== title)
        : [...prev, title]
    );
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const isActive = (path) => location.pathname === path;
  const isParentActive = (children) => children?.some(child => location.pathname.startsWith(child.path));

  const renderMenuItem = (item, mobile = false) => {
    const Icon = item.icon;
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedMenus.includes(item.title);
    const active = item.path ? isActive(item.path) : isParentActive(item.children);

    if (hasChildren) {
      return (
        <div key={item.title}>
          <button
            onClick={() => toggleMenu(item.title)}
            className={cn(
              'w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors',
              active
                ? 'bg-orange-100 text-orange-700'
                : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
            )}
          >
            <div className="flex items-center gap-3">
              <Icon className="w-5 h-5" />
              {(sidebarOpen || mobile) && <span>{item.title}</span>}
            </div>
            {(sidebarOpen || mobile) && (
              isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />
            )}
          </button>
          {(sidebarOpen || mobile) && isExpanded && (
            <div className="ml-4 mt-1 space-y-1">
              {item.children.map(child => {
                const ChildIcon = child.icon;
                return (
                  <Link
                    key={child.path}
                    to={child.path}
                    onClick={() => mobile && setMobileMenuOpen(false)}
                    className={cn(
                      'flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors',
                      isActive(child.path)
                        ? 'bg-orange-500 text-white'
                        : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                    )}
                  >
                    {ChildIcon && <ChildIcon className="w-4 h-4" />}
                    <span>{child.title}</span>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      );
    }

    return (
      <Link
        key={item.path}
        to={item.path}
        onClick={() => mobile && setMobileMenuOpen(false)}
        className={cn(
          'flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors',
          active
            ? 'bg-orange-500 text-white'
            : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
        )}
      >
        <Icon className="w-5 h-5" />
        {(sidebarOpen || mobile) && <span>{item.title}</span>}
      </Link>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile Menu Button */}
      <div className="lg:hidden fixed top-4 left-4 z-50">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="bg-white text-gray-700 hover:bg-gray-100 shadow-md"
        >
          {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </Button>
      </div>

      {/* Mobile Sidebar */}
      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 z-40 bg-black/20" onClick={() => setMobileMenuOpen(false)}>
          <div
            className="w-64 h-full bg-white border-r border-gray-200 p-4 shadow-xl"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 mb-8 mt-8">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center shadow-lg shadow-orange-200">
                <Store className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-gray-800">SY-Store</h1>
                <p className="text-xs text-gray-500">HQ Dashboard</p>
              </div>
            </div>
            <nav className="space-y-1">
              {menuItems.map(item => renderMenuItem(item, true))}
            </nav>
          </div>
        </div>
      )}

      {/* Desktop Sidebar */}
      <div
        className={cn(
          'hidden lg:fixed lg:inset-y-0 lg:flex lg:flex-col border-r border-gray-200 bg-white transition-all duration-300',
          sidebarOpen ? 'lg:w-64' : 'lg:w-20'
        )}
      >
        {/* Logo */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center shadow-lg shadow-orange-200">
              <Store className="w-5 h-5 text-white" />
            </div>
            {sidebarOpen && (
              <div>
                <h1 className="text-lg font-bold text-gray-800">SY-Store</h1>
                <p className="text-xs text-gray-500">HQ Dashboard</p>
              </div>
            )}
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="text-gray-500 hover:text-gray-700 hover:bg-gray-100"
          >
            {sidebarOpen ? <ChevronRight className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </Button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {menuItems.map(item => renderMenuItem(item))}
        </nav>

        {/* User Section */}
        <div className="p-4 border-t border-gray-200">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-gray-100 transition-colors">
                <Avatar className="w-9 h-9">
                  <AvatarFallback className="bg-gradient-to-br from-orange-500 to-amber-500 text-white text-sm">
                    {user?.full_name?.charAt(0) || 'U'}
                  </AvatarFallback>
                </Avatar>
                {sidebarOpen && (
                  <div className="flex-1 text-left">
                    <p className="text-sm font-medium text-gray-800 truncate">{user?.full_name}</p>
                    <p className="text-xs text-gray-500 truncate">{user?.role_name}</p>
                  </div>
                )}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 bg-white border-gray-200">
              <div className="px-2 py-1.5">
                <p className="text-sm font-medium text-gray-800">{user?.full_name}</p>
                <p className="text-xs text-gray-500">{user?.email}</p>
              </div>
              <DropdownMenuSeparator className="bg-gray-200" />
              <DropdownMenuItem
                onClick={() => setChangePasswordOpen(true)}
                className="cursor-pointer"
              >
                <KeyRound className="w-4 h-4 mr-2" />
                Change Password
              </DropdownMenuItem>
              <DropdownMenuSeparator className="bg-gray-200" />
              <DropdownMenuItem
                onClick={handleLogout}
                className="text-red-600 focus:text-red-600 focus:bg-red-50 cursor-pointer"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Sign Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Main Content */}
      <div className={cn(
        'transition-all duration-300',
        sidebarOpen ? 'lg:ml-64' : 'lg:ml-20'
      )}>
        <OnlineOrderBell />
        <BackupAlert />
        <Toaster position="top-right" richColors />
        <main className="min-h-screen">
          {children}
        </main>
      </div>
      
      {/* Change Password Dialog */}
      <ChangePasswordDialog 
        open={changePasswordOpen} 
        onOpenChange={setChangePasswordOpen} 
      />
    </div>
  );
};

export default HQLayout;
